import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Search, Filter, Users, MapPin, Star, Trophy } from 'lucide-react';
import '../../../assets/styles/customer/layout/DistributorsHeroSection.css';

const DistributorsHeroSection = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Stats with counter animation
  const [stats, setStats] = useState({
    distributors: 0,
    cities: 0,
    rating: 0,
    customers: 0
  });

  const finalStats = {
    distributors: 500,
    cities: 50,
    rating: 4.7,
    customers: 10000
  };

  // Intersection Observer for scroll detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  // Counter Animation
  useEffect(() => {
    if (!hasAnimated) return;

    const duration = 2000;
    const steps = 50;

    const animateCounter = (key, target) => {
      const increment = target / steps;
      let current = 0;
      let step = 0;

      const timer = setInterval(() => {
        step++;
        current = increment * step;
        
        if (step >= steps) {
          setStats(prev => ({ ...prev, [key]: target }));
          clearInterval(timer);
        } else {
          setStats(prev => ({ ...prev, [key]: current }));
        }
      }, duration / steps);
    };

    animateCounter('distributors', finalStats.distributors);
    animateCounter('cities', finalStats.cities);
    animateCounter('rating', finalStats.rating);
    animateCounter('customers', finalStats.customers);
  }, [hasAnimated]);

  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
  };

  const formatNumber = (num, decimals = 0) => {
    if (decimals > 0) {
      return num.toFixed(decimals);
    }
    return Math.floor(num).toLocaleString();
  };

  return (
    <section className="distributors-hero-section" ref={sectionRef}>
      
      {/* Floating Elements */}
      <div className="dist-hero-floating">
        <div className="float-circle hero-circle-1"></div>
        <div className="float-circle hero-circle-2"></div>
        <div className="float-circle hero-circle-3"></div>
        <div className="float-milk milk-1">🥛</div>
        <div className="float-milk milk-2">🥛</div>
        <div className="float-milk milk-3">🥛</div>
      </div>

      {/* Wavy Background */}
      <div className="dist-hero-waves">
        <svg className="dist-hero-wave wave-1" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path 
            fill="rgba(62, 150, 231, 0.05)" 
            d="M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,128C672,128,768,160,864,165.3C960,171,1056,149,1152,133.3C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </div>

      <Container>
        
        {/* Hero Content */}
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            
            <div className="dist-hero-content">
              
              {/* Badge */}
              <div className="dist-hero-badge">
                <Users size={18} />
                <span>Trusted Milk Distributors</span>
              </div>

              {/* Title */}
              <h1 className="dist-hero-title">
                Find Your Perfect <span className="dist-hero-highlight">Milk Distributor</span>
              </h1>

              {/* Description */}
              <p className="dist-hero-description">
                Browse through our network of verified distributors and get fresh, quality milk 
                delivered to your doorstep daily.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="dist-search-bar">
                <div className="search-input-wrapper">
                  <Search size={20} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search by name or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
                <button type="submit" className="filter-btn">
                  <Filter size={20} />
                  <span>Filter</span>
                </button>
              </form>

            </div>

          </Col>
        </Row>

        {/* Stats Cards */}
        <Row className="g-4 dist-stats-row">
          <Col lg={3} md={6} sm={6} xs={6}>
            <div className="dist-stat-card">
              <div className="stat-icon" style={{ color: '#3e96e7' }}>
                <Users size={32} strokeWidth={2} />
              </div>
              <div className="stat-number">{formatNumber(stats.distributors)}+</div>
              <div className="stat-label">Active Distributors</div>
            </div>
          </Col>

          <Col lg={3} md={6} sm={6} xs={6}>
            <div className="dist-stat-card">
              <div className="stat-icon" style={{ color: '#4caf50' }}>
                <MapPin size={32} strokeWidth={2} />
              </div>
              <div className="stat-number">{formatNumber(stats.cities)}+</div>
              <div className="stat-label">Cities Covered</div>
            </div>
          </Col>

          <Col lg={3} md={6} sm={6} xs={6}>
            <div className="dist-stat-card">
              <div className="stat-icon" style={{ color: '#ffc107' }}>
                <Star size={32} strokeWidth={2} />
              </div>
              <div className="stat-number">{formatNumber(stats.rating, 1)}</div>
              <div className="stat-label">Average Rating</div>
            </div>
          </Col>

          <Col lg={3} md={6} sm={6} xs={6}>
            <div className="dist-stat-card">
              <div className="stat-icon" style={{ color: '#9c27b0' }}>
                <Trophy size={32} strokeWidth={2} />
              </div>
              <div className="stat-number">{(stats.customers / 1000).toFixed(0)}K+</div>
              <div className="stat-label">Happy Customers</div>
            </div>
          </Col>
        </Row>

      </Container>
    </section>
  );
};

export default DistributorsHeroSection;